import { memo, useState } from "react";
import {
  CheckIcon,
  ClipboardDocumentIcon,
  CodeBracketIcon,
} from "@";

/**
 * Code-friendly textarea for tracking script snippets.
 * - Monospace font, slight bg tint, LTR direction inside (scripts are English).
 * - Live character count + "looks like a script" hint when content includes `<script`.
 * - Copy button (only visible when there's content).
 *
 * @param {{
 *   label: string,
 *   hint?: string,
 *   placeholder?: string,
 *   rows?: number,
 *   input: object,                  // react-hook-form `register("...")` result
 *   value: string,                  // current value (read from `watch`)
 *   error?: string,
 * }} props
 */
const ScriptField = ({
  label,
  hint,
  placeholder,
  rows = 6,
  input,
  value = "",
  error,
}) => {
  const [copied, setCopied] = useState(false);
  const trimmed = (value || "").trim();
  const hasContent = trimmed.length > 0;
  const looksLikeScript = /<script[\s>]/i.test(trimmed);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      /* clipboard blocked — ignore silently */
    }
  };

  return (
    <div>
      {/* Label row */}
      <div className="mb-1.5 flex items-end justify-between gap-2">
        <label className="flex items-center gap-1.5 text-[13px] font-semibold tracking-tight text-gray-800 dark:text-gray-200">
          <CodeBracketIcon className="h-3.5 w-3.5 text-purple-700 dark:text-purple-400" strokeWidth={2.25} />
          {label}
        </label>

        {hasContent && (
          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold text-gray-500 transition-colors hover:bg-purple-50 hover:text-purple-900 dark:hover:bg-purple-950/40 dark:hover:text-purple-300"
            title="نسخ الشيفرة"
          >
            {copied ? (
              <>
                <CheckIcon className="h-3 w-3" strokeWidth={2.5} />
                تم النسخ
              </>
            ) : (
              <>
                <ClipboardDocumentIcon className="h-3 w-3" strokeWidth={2} />
                نسخ
              </>
            )}
          </button>
        )}
      </div>

      {/* Hint */}
      {hint && (
        <p className="mb-1.5 text-[11px] leading-snug text-gray-500 dark:text-gray-400">
          {hint}
        </p>
      )}

      {/* Textarea */}
      <div
        className={`group relative overflow-hidden rounded-2xl border bg-gray-50/60 transition-colors focus-within:bg-white dark:bg-gray-900/40 dark:focus-within:bg-gray-900 ${
          error
            ? "border-rose-300 focus-within:border-rose-400 dark:border-rose-800"
            : hasContent
              ? "border-green-200 focus-within:border-purple-300 dark:border-green-800/50 dark:focus-within:border-purple-700"
              : "border-gray-200 focus-within:border-purple-300 dark:border-gray-700 dark:focus-within:border-purple-700"
        }`}
      >
        <textarea
          {...input}
          rows={rows}
          dir="ltr"
          spellCheck={false}
          placeholder={placeholder}
          className="input rounded-b-none"
        />

        {/* Footer: status + char count */}
        <div className="flex items-center justify-between gap-2 border-t border-gray-100 bg-white/60 px-3 py-1.5 text-[10px] font-medium dark:border-gray-700/60 dark:bg-gray-900/40">
         
          <span className="text-gray-400 tabular-nums dark:text-gray-500">
            {trimmed.length.toLocaleString("ar-EG")} حرف
          </span>
        </div>
      </div>

      {error && (
        <p className="mt-1 text-[11px] text-rose-600 dark:text-rose-400">
          {error}
        </p>
      )}
    </div>
  );
};

export default memo(ScriptField);
